import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

// Function to set Axios auth header
const setAuthHeader = (token) => {
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    delete axios.defaults.headers.common['Authorization']
  }
}

const state = {
  token: localStorage.getItem('token') || null,
  user: JSON.parse(localStorage.getItem('user')) || null,
  isSubscribed: false
}

// Initialize Axios header if token exists on load
setAuthHeader(state.token);

const getters = {
  isAuthenticated: state => !!state.token,
  user: state => state.user,
  token: state => state.token,
  isUserSubscribed: state => state.isSubscribed
}

const actions = {
  async login({ commit, dispatch }, initData) {
    try {
      const response = await axios.post(`${API_URL}/telegram/auth/`, { init_data: initData })
      const { token, user } = response.data

      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      setAuthHeader(token)

      commit('SET_AUTH', { token, user })
      dispatch('checkSubscriptionStatus') // Check subscription after login
      return true
    } catch (error) {
      console.error('Authentication error:', error)
      commit('CLEAR_AUTH') // Clear auth state on login error
      return false
    }
  },

  // Add a new action for setting a test token
  setTestToken({ commit }, { token, user = null }) {
    const testUser = user || { id: 'test-user', free_messages_sent: 0 } // Provide default test user data
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(testUser))
    setAuthHeader(token)

    commit('SET_AUTH', { token, user: testUser })
    commit('SET_SUBSCRIPTION_STATUS', false) // Reset subscription for test user
    return true
  },

  logout({ commit }) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setAuthHeader(null) // Remove auth header

    commit('CLEAR_AUTH')
    commit('SET_SUBSCRIPTION_STATUS', false)
  },

  async checkSubscriptionStatus({ commit, state }) {
    if (!state.token) {
      commit('SET_SUBSCRIPTION_STATUS', false);
      return;
    }
    try {
      // Assuming the status endpoint doesn't require auth or uses the set header
      const response = await axios.get(`${API_URL}/subscriptions/status/`);
      commit('SET_SUBSCRIPTION_STATUS', response.data.isSubscribed);
    } catch (error) {
      console.error('Error checking subscription status:', error);
      // Don't necessarily assume false, could be a temporary network error
      // Consider if state.isSubscribed should be reset here or handled differently
       commit('SET_SUBSCRIPTION_STATUS', false); // Keep existing behavior for now
    }
  },

  // New action to fetch current user data using the existing token
  async fetchCurrentUser({ commit, state, dispatch }) {
    if (!state.token) {
      console.log('No token found, skipping fetchCurrentUser.')
      return; // No token, nothing to fetch
    }
    try {
      // Assume an endpoint like /users/me/ returns the current user's data
      const response = await axios.get(`${API_URL}/users/me/`);
      const user = response.data;

      localStorage.setItem('user', JSON.stringify(user)); // Update local storage too
      commit('SET_AUTH', { user }); // Update only the user in the state

      // Optionally, re-check subscription status if it depends on user data
      // or if the /users/me endpoint includes subscription status
       if (user.has_active_subscription !== undefined) {
           commit('SET_SUBSCRIPTION_STATUS', user.has_active_subscription);
       } else {
           // If status not included in user data, check it separately
           dispatch('checkSubscriptionStatus');
       }

    } catch (error) {
      console.error('Error fetching current user:', error);
      // If the error indicates an invalid/expired token (e.g., 401 Unauthorized)
      // Log the user out
      if (error.response && error.response.status === 401) {
        console.log('Token invalid, logging out.');
        dispatch('logout');
      }
      // Handle other errors as needed
    }
  }
}

const mutations = {
  // Modified SET_AUTH to handle partial updates (only user or only token)
  SET_AUTH(state, { token, user }) {
    if (token !== undefined) {
      state.token = token;
    }
    if (user !== undefined) {
      state.user = user;
    }
  },

  CLEAR_AUTH(state) {
    state.token = null
    state.user = null
  },

  SET_SUBSCRIPTION_STATUS(state, status) {
    state.isSubscribed = !!status
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 